/**
 * The signed-in user's resolved permissions.
 *
 * Fetched once after sign-in and used to hide what the user cannot do. That is a
 * usability decision only: **the API enforces every one of these independently**, and
 * an endpoint behaves correctly when called directly regardless of what this client
 * chose to render.
 *
 * The API returns resolved permissions rather than the rules that produced them, so
 * there is no policy here to get wrong or to tamper with.
 */

import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api/client'
import { queryKeys } from '@/lib/query'

export interface Session {
  userId: string
  email: string
  displayName: string
  principalType: string
  roles: string[]
  permissions: string[]
  visibility: {
    unrestricted: boolean
    scopes: { type: string; value: string }[]
  }
}

export function useSession() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: async (): Promise<Session> => {
      const { data } = await api.GET('/api/v1/me')
      return data as unknown as Session
    },
    // Permissions change rarely, and a stale permission only ever affects what is shown,
    // never what is allowed.
    staleTime: 5 * 60_000,
  })
}

export function usePermission(...permissions: string[]): boolean {
  const { data } = useSession()
  if (!data) return false
  return permissions.some((permission) => data.permissions.includes(permission))
}

/** Permission codes, mirroring wheelerpmt/auth/permissions.py. */
export const Permissions = {
  projectsView: 'projects.view',
  projectsCreate: 'projects.create',
  projectsEdit: 'projects.edit',
  projectsDelete: 'projects.delete',
  projectsTransitionStage: 'projects.transition_stage',
  projectsClose: 'projects.close',
  prioritiesView: 'priorities.view',
  prioritiesScore: 'priorities.score',
  workView: 'work.view',
  workEdit: 'work.edit',
  workManageSprints: 'work.manage_sprints',
  taxonomyManage: 'taxonomy.manage',
  documentsView: 'documents.view',
  documentsManage: 'documents.manage',
  documentsSignOff: 'documents.sign_off',
  reportsView: 'reports.view',
  reportsShare: 'reports.share',
  feedbackSubmit: 'feedback.submit',
  feedbackTriage: 'feedback.triage',
  adminAccess: 'admin.access',
  adminSettingsView: 'admin.settings.view',
  adminSettingsEdit: 'admin.settings.edit',
  adminProvidersView: 'admin.providers.view',
  adminProvidersEdit: 'admin.providers.edit',
  adminUsersView: 'admin.users.view',
  adminUsersEdit: 'admin.users.edit',
  adminRolesEdit: 'admin.roles.edit',
  adminFlagsEdit: 'admin.flags.edit',
  adminClientsEdit: 'admin.clients.edit',
  adminAuditView: 'admin.audit.view',
  adminHealthView: 'admin.health.view',
  adminSyncView: 'admin.sync.view',
  adminSyncEdit: 'admin.sync.edit',
} as const
